import { forwardRef, useId } from "react";
import type { InputHTMLAttributes, ReactNode } from "react";

// 文字輸入欄。一律有可見標籤或 aria-label,不靠 placeholder 當標籤。

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** 可見標籤。若省略,呼叫端必須自行給 aria-label。 */
  label?: ReactNode;
  /** 標籤下方的說明文字。 */
  hint?: ReactNode;
  /** 錯誤訊息。有值時欄位標成錯誤狀態並用 aria-describedby 連起來。 */
  error?: string | null;
  /**
   * 把錯誤訊息宣告成即時區域(`role="alert"`)。
   *
   * 預設不宣告:`aria-describedby` 已經夠用 —— 焦點移到那一格就聽得到原因。
   * 但錯誤是**別的欄位造成的**、或是在使用者還沒碰到那一格之前就出現的時候
   * (例如「迄日不能早於起日」在改完迄日的當下就成立),不宣告就等於沒說 ——
   * 他要等到 Tab 走回去才知道。這種情形才打開它。
   */
  errorRole?: "alert";
  /** 金額或數量欄:靠右並用表格數字對齊。 */
  numeric?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, errorRole, numeric = false, className = "", id, ...rest },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;

  return (
    <div className="flex min-w-0 flex-col gap-1">
      {label ? (
        <label htmlFor={inputId} className="text-sm font-medium text-[var(--text-2)]">
          {label}
        </label>
      ) : null}
      <input
        {...rest}
        id={inputId}
        ref={ref}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={
          "h-9 w-full min-w-0 rounded-[var(--radius)] border bg-[var(--surface)] px-2.5 text-sm " +
          "text-[var(--text)] placeholder:text-[var(--text-3)] " +
          (error ? "border-[var(--danger)] " : "border-[var(--border-strong)] ") +
          (numeric ? "text-right tabular " : "") +
          className
        }
      />
      {hint ? (
        <span id={hintId} className="text-xs text-[var(--text-3)]">
          {hint}
        </span>
      ) : null}
      {error ? (
        <span id={errorId} role={errorRole} className="text-xs text-[var(--danger)]">
          {error}
        </span>
      ) : null}
    </div>
  );
});
